import {buildTourProgressElement} from './product-tours.js'

export const CPM_RETIREMENT_TOUR_EVENT = 'cpm:tour:old-cpm-retirement'
export const CPM_RETIREMENT_TOUR_STORAGE_KEY = 'cpm:tour:old-cpm-retirement'

const switcherSelector = '[data-cpm-retirement-tour]'
const newNovaSelector = '[data-cy="new-cpm-nova-link"]'

function stepText(documentObject, index, message) {
    const content = documentObject.createElement('div')
    content.append(buildTourProgressElement(index, 3, documentObject))

    const body = documentObject.createElement('p')
    body.className = 'nurse-performance-tour-body'
    body.textContent = message
    content.append(body)

    return content
}

function csrfToken(documentObject) {
    return documentObject.querySelector('meta[name="csrf-token"]')?.content ?? ''
}

async function post(browserWindow, documentObject, url) {
    return browserWindow.fetch(url, {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken(documentObject),
        },
        method: 'POST',
    })
}

export function startCpmRetirementTour(browserWindow, documentObject, ShepherdLibrary) {
    const switcher = documentObject.querySelector(switcherSelector)
    const dropdown = switcher?.querySelector('.dropdown-container')
    const trigger = switcher?.querySelector('[dropdown-trigger]')
    const newNovaLink = switcher?.querySelector(newNovaSelector)
    const completeUrl = switcher?.dataset.tourCompleteUrl

    if (!switcher || !dropdown || !trigger || !newNovaLink || !completeUrl) {
        return false
    }

    const tour = new ShepherdLibrary.Tour({
        defaultStepOptions: {
            cancelIcon: {enabled: true},
            classes: 'nurse-performance-tour',
            modalOverlayOpeningPadding: 10,
            modalOverlayOpeningRadius: 16,
            scrollTo: {behavior: 'smooth', block: 'center'},
        },
        exitOnEsc: true,
        exitOnOverlayClick: true,
        useModalOverlay: true,
    })

    let dropdownOpened = false

    tour.addStep({
        id: 'old-cpm-retired',
        attachTo: {element: switcherSelector, on: 'bottom-start'},
        buttons: [
            {action: () => tour.cancel(), classes: 'shepherd-button-secondary', text: 'Not now'},
            {action: async () => {
                try {
                    const response = await post(browserWindow, documentObject, completeUrl)
                    if (!response.ok) throw new Error('Tour completion failed')
                    tour.next()
                } catch {
                    browserWindow.alert('Could not save your tour choice. Please try again.')
                }
            }, text: 'Take the tour'},
        ],
        text: stepText(documentObject, 0, 'Old CPM has retired. Old CPM Nova remains available while its remaining work moves gradually into New CPM.'),
        title: 'New CPM is now your center point',
    })

    tour.addStep({
        id: 'apps-menu',
        attachTo: {element: switcherSelector, on: 'bottom-start'},
        beforeShowPromise: () => {
            dropdown.setAttribute('data-tour', 'true')
            trigger.click()
            dropdownOpened = true
            return Promise.resolve()
        },
        buttons: [
            {action: () => tour.back(), classes: 'shepherd-button-secondary', text: 'Back'},
            {action: () => tour.next(), text: 'Next'},
        ],
        text: stepText(documentObject, 1, 'Use Apps to move between CircleLink tools. It unifies your experience so New CPM can be the starting point for your work.'),
        title: 'Everything in one place',
    })

    tour.addStep({
        id: 'new-cpm-nova',
        attachTo: {element: newNovaSelector, on: 'right'},
        buttons: [
            {action: () => tour.back(), classes: 'shepherd-button-secondary', text: 'Back'},
            {
                action: () => {
                    browserWindow.sessionStorage.setItem(CPM_RETIREMENT_TOUR_STORAGE_KEY, '0')
                    browserWindow.location.assign(newNovaLink.href)
                },
                text: 'Continue in New CPM Nova',
            },
        ],
        text: stepText(documentObject, 2, 'Next, we’ll visit the two ABP pages and three EHR Sync pages that moved here from CPM1.'),
        title: 'Open New CPM Nova',
    })

    const cleanup = () => {
        dropdown.removeAttribute('data-tour')
        if (dropdownOpened) trigger.click()
    }

    tour.on('complete', cleanup)
    tour.on('cancel', cleanup)
    browserWindow.setTimeout(() => tour.start(), 200)

    return true
}

export function registerCpmRetirementTour(browserWindow, documentObject, ShepherdLibrary) {
    let started = false
    const start = () => {
        if (!started) {
            started = startCpmRetirementTour(browserWindow, documentObject, ShepherdLibrary)
        }
    }

    browserWindow.addEventListener(CPM_RETIREMENT_TOUR_EVENT, start)

    const switcher = documentObject.querySelector(switcherSelector)
    const claimUrl = switcher?.dataset.tourClaimUrl

    if (claimUrl) {
        post(browserWindow, documentObject, claimUrl)
            .then(response => response.ok ? response.json() : {show: false})
            .then(result => {
                if (result.show) start()
            })
            .catch(() => {})
    }

    return () => browserWindow.removeEventListener(CPM_RETIREMENT_TOUR_EVENT, start)
}
